import React, { useState, useEffect, useCallback, useRef } from 'react';
import Icon from '../comps/Icon';
import reservationsSvc from '../services/reservations';
import { toMinutes } from '../services/seatingEngine';
import '../style/css/waitlist2026.css';

/*
 * WaitlistPage — the digital queue (רשימת המתנה).
 * A Starter feature: a walk-in waitlist that works WITHOUT a floor map or table numbers.
 * Wired to the real backend via reservationsSvc (waitingList / create / setStatus / waitingRemove).
 *
 * Flow:
 *  - loads waitingList() on mount, on a 30s poll, and on the global hoppa:reservations-changed event.
 *  - an add form pushes a new waiting entry (create({ waiting: 1 })).
 *  - each queued guest can be marked הגיע / הושב (seated) or removed (עזב), all real DB writes.
 *  - a live 30s "now" tick keeps the "ממתין X דק׳" wait times fresh.
 */

const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const nowClock = () => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// Lenient phone check — accept IL 0XXXXXXXX(X) or an international +… number. Optional field,
// so an empty string is always valid; only a clearly-nonsense value is rejected.
const phoneLooksOk = (raw) => {
    const v = (raw || '').trim();
    if (!v) return true;
    const digits = v.replace(/[^\d]/g, '');
    if (v.startsWith('+')) return digits.length >= 8 && digits.length <= 15;
    return /^0\d{7,9}$/.test(digits);
};

const EMPTY = { name: '', phone: '', party: '2' };

export default function WaitlistPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);
    const [now, setNow] = useState(() => new Date());
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState('');
    // per-entry in-flight guard (ids currently being mutated) so an action can't double-fire.
    const busyRef = useRef(new Set());
    const [, force] = useState(0);
    const rerender = () => force((n) => n + 1);

    // ── load / poll / event ──
    // mountedRef gates every setState in the async load so an in-flight request that
    // resolves after unmount can't commit to a dead component.
    const mountedRef = useRef(true);
    const load = useCallback(async () => {
        // waitingListResult signals real load failures ({ ok:false }) so a backend outage
        // shows the error state instead of a false "אין ממתינים כרגע" empty list.
        const res = await reservationsSvc.waitingListResult();
        if (!mountedRef.current) return;
        if (res.ok) {
            // waitingList already returns oldest-first; keep that order.
            setRows(Array.isArray(res.rows) ? res.rows : []);
            setErrored(false);
        } else {
            setErrored(true);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        load();
        const poll = setInterval(load, 30000);
        const onChange = () => { load(); };
        window.addEventListener('hoppa:reservations-changed', onChange);
        return () => { mountedRef.current = false; clearInterval(poll); window.removeEventListener('hoppa:reservations-changed', onChange); };
    }, [load]);

    // live "now" tick so wait times update without a reload.
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 30000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        if (!toast) return undefined;
        const t = setTimeout(() => setToast(''), 3000);
        return () => clearTimeout(t);
    }, [toast]);

    const notify = () => window.dispatchEvent(new Event('hoppa:reservations-changed'));

    // ── add to waitlist ──
    const nameOk = form.name.trim().length > 0;
    const phoneOk = phoneLooksOk(form.phone);
    const canSubmit = nameOk && phoneOk && !saving;

    const submitAdd = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSaving(true);
        const res = await reservationsSvc.create({
            date: todayISO(),
            startTime: `${nowClock()}:00`,
            numberOfGuests: Math.max(1, Number(form.party) || 1),
            guestName: form.name.trim(),
            guestPhone: form.phone.trim(),
            waiting: 1,
        });
        setSaving(false);
        if (res && res.ok) {
            setForm(EMPTY);
            setToast('נוסף לתור ההמתנה');
            await load();
            notify();
        } else {
            setToast('לא ניתן להוסיף — נסו שוב');
        }
    };

    // ── per-entry actions (each guarded against double-fire) ──
    const runOnce = async (id, fn, okMsg, failMsg) => {
        if (busyRef.current.has(id)) return;
        busyRef.current.add(id);
        rerender();
        try {
            const res = await fn();
            if (res && res.ok) {
                setToast(okMsg);
                await load();
                notify();
            } else {
                setToast(failMsg);
                await load();
            }
        } catch (e) {
            setToast(failMsg);
        } finally {
            busyRef.current.delete(id);
            rerender();
        }
    };

    const markArrived = (id) =>
        runOnce(id, () => reservationsSvc.setStatus({ reservationId: id, status: 'arrived' }), 'סומן שהגיע', 'הפעולה נכשלה — נסו שוב');

    const markSeated = (id) => {
        // optimistic remove — they leave the queue the moment they're seated.
        setRows((prev) => prev.filter((r) => String(r.id) !== String(id)));
        runOnce(id, () => reservationsSvc.setStatus({ reservationId: id, status: 'seated' }), 'הושב', 'לא ניתן להושיב — נסו שוב');
    };

    const removeEntry = (id, name) => {
        if (!window.confirm(`להסיר את ${name || 'האורח'} מתור ההמתנה?`)) return;
        setRows((prev) => prev.filter((r) => String(r.id) !== String(id)));
        runOnce(id, () => reservationsSvc.waitingRemove(id), 'הוסר מהתור', 'לא ניתן להסיר — נסו שוב');
    };

    // ── wait time (minutes since join, guarded ≥0) ──
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const waitedMinutes = (time) => {
        if (!time) return 0;
        const parsed = toMinutes(time);
        // A malformed time yields NaN; render 0 rather than "NaN דק׳".
        return Number.isFinite(parsed) ? Math.max(0, nowMinutes - parsed) : 0;
    };
    const clock = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

    return (
        <div className="wl-page">
            <div className="wl-head">
                <h1 className="wl-head-title">
                    <Icon className="wl-head-ico" name="list" size={24} />
                    רשימת המתנה
                    {rows.length > 0 && <span className="wl-count">{rows.length}</span>}
                </h1>
                <span className="wl-clock">
                    <Icon className="wl-clock-ico" name="clock" size={16} />
                    <span dir="ltr">{clock}</span>
                    <span>· {rows.length} ממתינים</span>
                </span>
            </div>

            {/* add-to-waitlist form */}
            <div className="wl-addcard">
                <form className="wl-addform" onSubmit={submitAdd}>
                    <label className="wl-field wl-field--name">
                        שם האורח
                        <input
                            value={form.name}
                            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                            placeholder="שם מלא"
                            aria-label="שם האורח"
                        />
                    </label>
                    <label className="wl-field">
                        טלפון (רשות)
                        <input
                            className={form.phone && !phoneOk ? 'wl-invalid' : ''}
                            value={form.phone}
                            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                            placeholder="050-0000000"
                            dir="ltr"
                            inputMode="tel"
                            aria-label="טלפון"
                        />
                    </label>
                    <label className="wl-field">
                        סועדים
                        <input
                            type="number"
                            min="1"
                            max="60"
                            value={form.party}
                            onChange={(e) => setForm((f) => ({ ...f, party: e.target.value }))}
                            aria-label="מספר סועדים"
                        />
                    </label>
                    <button className="wl-add-btn" type="submit" disabled={!canSubmit}>
                        <Icon name="plus" size={16} /> {saving ? 'מוסיף…' : 'הוסף לתור'}
                    </button>
                </form>
            </div>

            {/* the queue */}
            {loading ? (
                <div className="wl-state">טוען…</div>
            ) : errored ? (
                <div className="wl-state">שגיאה בטעינת התור — נסו שוב.</div>
            ) : rows.length === 0 ? (
                <div className="wl-state">אין ממתינים כרגע</div>
            ) : (
                <ul className="wl-list">
                    {rows.map((w, i) => {
                        const wm = waitedMinutes(w.time);
                        const busy = busyRef.current.has(w.id);
                        return (
                            <li className="wl-card" key={w.id}>
                                <span className="wl-pos">#{i + 1}</span>
                                <div className="wl-body">
                                    <span className="wl-name">{w.name || '—'}</span>
                                    <span className="wl-meta">
                                        <span className="wl-meta-item">
                                            <Icon className="wl-meta-ico" name="users" size={13} />
                                            {w.party || 1}
                                        </span>
                                        {w.phone && (
                                            <span className="wl-meta-item" dir="ltr">
                                                <Icon className="wl-meta-ico" name="phone" size={13} />
                                                {w.phone}
                                            </span>
                                        )}
                                        {w.time && (
                                            <span className="wl-meta-item">
                                                <Icon className="wl-meta-ico" name="clock" size={13} />
                                                <span dir="ltr">{String(w.time).slice(0, 5)}</span>
                                            </span>
                                        )}
                                    </span>
                                </div>
                                <span className={`wl-wait ${wm >= 30 ? 'wl-wait--long' : ''}`}>
                                    <Icon name="clock" size={13} /> ממתין {wm} דק׳
                                </span>
                                <span className="wl-actions">
                                    <button type="button" className="wl-btn wl-btn--ghost" disabled={busy}
                                        onClick={() => markArrived(w.id)}>הגיע</button>
                                    <button type="button" className="wl-btn wl-btn--primary" disabled={busy}
                                        onClick={() => markSeated(w.id)}>שולחן מוכן / הושב</button>
                                    <button type="button" className="wl-btn wl-btn--danger" disabled={busy}
                                        onClick={() => removeEntry(w.id, w.name)}>עזב / הסר</button>
                                </span>
                            </li>
                        );
                    })}
                </ul>
            )}

            {toast && <div className="wl-toast">{toast}</div>}
        </div>
    );
}
